import { DOCUMENT } from '@angular/common';
import { ElementRef, Inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { CdkScrollable, ScrollDispatcher } from '@angular/cdk/scrolling';

const ACTIONS = '.ant-select-clear, .ant-select-selection-item-remove, .ant-picker-clear, .ant-input-clear-icon, .ant-notification-notice-close';
const FIELDS = 'nz-select, nz-date-picker, nz-range-picker, nz-time-picker, .ant-input-affix-wrapper';
const DISABLED = '.ant-select-disabled, .ant-picker-disabled, .ant-input-affix-wrapper-disabled, [disabled], [aria-disabled="true"]';
interface PopupControl { isOpen: () => boolean; close: () => void; refresh?: () => void; }

/** Accessibility adapter for actions rendered inside NG-ZORRO 16 templates.
 * Keep existing library click handlers as the single source of model changes.
 * Also registers dynamically-created modal/scroll containers with CDK.
 */
@Injectable({ providedIn: 'root' })
export class ControlAccessibilityService implements OnDestroy {
  private observer?: MutationObserver;
  private scrollResize?: ResizeObserver;
  private frame = 0;
  private activationKey: string | null = null;
  private readonly scrollables = new Map<HTMLElement, CdkScrollable>();
  private readonly popups = new Map<HTMLElement, PopupControl>();
  private win: Window | null;

  constructor(@Inject(DOCUMENT) private document: Document, private zone: NgZone,
    private dispatcher: ScrollDispatcher) { this.win = document.defaultView; }

  registerPopup(field: HTMLElement, popup: PopupControl): () => void {
    this.popups.set(field, popup);
    return () => this.popups.delete(field);
  }

  start(): void {
    if (this.observer || !this.win) return;
    this.zone.runOutsideAngular(() => {
      this.scrollResize = new ResizeObserver(this.viewportResize);
      this.observer = new MutationObserver(this.schedule);
      this.observer.observe(this.document.body, { childList: true, subtree: true,
        attributes: true, attributeFilter: ['class', 'disabled', 'dir'] });
      this.document.addEventListener('keydown', this.keydown, true);
      this.document.addEventListener('keyup', this.keyup, true);
      this.document.addEventListener('click', this.click, true);
      this.document.addEventListener('pointerdown', this.prepareScroll, true);
      this.document.addEventListener('focusin', this.prepareScroll, true);
      this.document.addEventListener('scroll', this.onScroll, true);
      this.win!.visualViewport?.addEventListener('resize', this.viewportResize);
      this.win!.visualViewport?.addEventListener('scroll', this.onScroll);
      this.schedule();
    });
  }

  private schedule = (): void => {
    if (!this.frame) this.frame = this.win!.requestAnimationFrame(() => {
      this.frame = 0;
      this.enhance();
    });
  };

  private enhance(): void {
    const arabic = this.document.documentElement.dir === 'rtl';
    this.document.querySelectorAll<HTMLElement>(ACTIONS).forEach(action => {
      const disabled = !!action.closest(FIELDS)?.closest(DISABLED);
      action.setAttribute('role', 'button');
      action.tabIndex = disabled ? -1 : 0;
      action.setAttribute('aria-disabled', String(disabled));
      const remove = action.classList.contains('ant-select-selection-item-remove');
      const item = action.closest('.ant-select-selection-item')?.querySelector('.ant-select-selection-item-content')?.textContent?.trim();
      const close = action.classList.contains('ant-notification-notice-close');
      action.setAttribute('aria-label', close ? (arabic ? 'إغلاق' : 'Close') : remove ? `${arabic ? 'إزالة' : 'Remove'} ${item || ''}`.trim() : arabic ? 'مسح القيمة' : 'Clear value');
    });
    this.document.querySelectorAll<HTMLElement>('.ant-modal-close, .toast-close-button').forEach(button => button.setAttribute('aria-label', arabic ? 'إغلاق' : 'Close'));
    this.document.querySelectorAll<HTMLElement>('.ant-upload-list-item-card-actions-btn').forEach(button => {
      if (button.querySelector('.anticon-delete')) button.setAttribute('aria-label', arabic ? 'إزالة المرفق' : 'Remove attachment');
    });
    this.document.querySelectorAll<HTMLElement>('nz-select[aria-label]').forEach(select => {
      select.querySelector('input')?.setAttribute('aria-label', select.getAttribute('aria-label')!);
    });
    // These containers are created by the modal library, outside Angular app templates.
    this.document.querySelectorAll<HTMLElement>('.ant-modal-wrap, .ant-modal-body, .modal-overlay, .modal-content, .wh-modal-body').forEach(el => this.register(el));
    for (const [el, scrollable] of this.scrollables) {
      if (!el.isConnected) { scrollable.ngOnDestroy(); this.scrollResize?.unobserve(el); this.scrollables.delete(el); }
    }
    this.closeHiddenTriggers();
    // Time-picker panels are recreated after the open event/exit animation.
    // Refresh their owner after portal DOM changes, rather than timing a delay.
    this.popups.forEach(popup => { if (popup.isOpen()) popup.refresh?.(); });
  }

  private target(event: Event): HTMLElement | null {
    return event.target instanceof Element ? event.target.closest<HTMLElement>(ACTIONS) : null;
  }
  private keydown = (event: KeyboardEvent): void => {
    const action = this.target(event);
    if (!action || (event.key !== 'Enter' && event.key !== ' ')) {
      if (!event.repeat) this.activationKey = null;
      return;
    }
    this.activationKey = event.key;
    event.preventDefault(); event.stopImmediatePropagation();
    if (!event.repeat && !action.closest(FIELDS)?.closest(DISABLED)) this.zone.run(() => action.click());
  };
  private keyup = (event: KeyboardEvent): void => {
    if (event.key !== this.activationKey) return;
    this.activationKey = null;
    // Focus moved to the input on keydown. Do not let its Enter keyup reopen
    // a cleared picker or submit its surrounding form a second time.
    event.preventDefault(); event.stopImmediatePropagation();
  };
  private click = (event: MouseEvent): void => {
    const action = this.target(event);
    if (!action) return;
    if (action.closest(FIELDS)?.closest(DISABLED)) { event.preventDefault(); event.stopImmediatePropagation(); return; }
    const input = action.closest(FIELDS)?.querySelector<HTMLInputElement>('input:not([disabled])');
    // Move focus before the focused clear/tag node is removed. Losing focus
    // during Angular's render changes NG-ZORRO's focused binding mid-check.
    if (input?.isConnected) input.focus({ preventScroll: true });
  };

  private register(el: HTMLElement): void {
    if (this.scrollables.has(el) || el.hasAttribute('cdkScrollable') || el.classList.contains('cdk-virtual-scroll-viewport')) return;
    const overflow = this.win!.getComputedStyle(el);
    if (!/(auto|scroll)/.test(`${overflow.overflowX} ${overflow.overflowY}`)) return;
    const scrollable = new CdkScrollable(new ElementRef(el), this.dispatcher, this.zone);
    scrollable.ngOnInit();
    this.scrollables.set(el, scrollable);
    this.scrollResize?.observe(el);
  }
  private prepareScroll = (event: Event): void => {
    let el = event.target instanceof HTMLElement ? event.target : null;
    while (el && el !== this.document.body) { this.register(el); el = el.parentElement; }
  };
  private onScroll = (): void => { this.closeHiddenTriggers(); };
  private viewportResize = (): void => {
    // CDK's ViewportRuler listens to window resize; mobile keyboard changes can
    // emit only on visualViewport. Let CDK recalculate, never position with CSS.
    this.win!.dispatchEvent(new Event('resize'));
    this.closeHiddenTriggers();
  };
  private closeHiddenTriggers(): void {
    this.popups.forEach((popup, field) => {
      if (popup.isOpen() && this.isHidden(field)) this.zone.run(popup.close);
    });
    // Non-searchable, library-generated pagination/calendar selects are outside
    // application directive scopes. Their host click toggles the open menu;
    // keep it local so it cannot dismiss the containing dialog.
    this.document.querySelectorAll<HTMLElement>('nz-select.ant-select-open:not(.ant-select-show-search)').forEach(field => {
      if (!this.popups.has(field) && this.isHidden(field)) this.zone.run(() => {
        field.dispatchEvent(new MouseEvent('click', { bubbles: false }));
      });
    });
  }
  private isHidden(field: HTMLElement): boolean {
    const rect = field.getBoundingClientRect();
    const viewport = this.win!.visualViewport;
    const top = viewport?.offsetTop || 0, left = viewport?.offsetLeft || 0;
    let hidden = !field.getClientRects().length || rect.bottom <= top || rect.top >= top + (viewport?.height || this.win!.innerHeight) || rect.right <= left || rect.left >= left + (viewport?.width || this.win!.innerWidth);
    for (let parent = field.parentElement; parent && !hidden; parent = parent.parentElement) {
      if (this.scrollables.has(parent)) {
        const clip = parent.getBoundingClientRect();
        hidden = rect.bottom <= clip.top || rect.top >= clip.bottom || rect.right <= clip.left || rect.left >= clip.right;
      }
    }
    return hidden;
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.scrollResize?.disconnect();
    if (this.frame) this.win?.cancelAnimationFrame(this.frame);
    this.document.removeEventListener('keydown', this.keydown, true);
    this.document.removeEventListener('keyup', this.keyup, true);
    this.document.removeEventListener('click', this.click, true);
    this.document.removeEventListener('pointerdown', this.prepareScroll, true);
    this.document.removeEventListener('focusin', this.prepareScroll, true);
    this.document.removeEventListener('scroll', this.onScroll, true);
    this.win?.visualViewport?.removeEventListener('resize', this.viewportResize);
    this.win?.visualViewport?.removeEventListener('scroll', this.onScroll);
    this.scrollables.forEach(scrollable => scrollable.ngOnDestroy());
    this.scrollables.clear();
    this.popups.clear();
  }
}
